import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  theme as chakraTheme,
  useMediaQuery,
} from "@chakra-ui/react";
import { theme } from "@ovou-inc/ovou-ui";
import type { Area, Point } from "react-easy-crop";
import { useToasts } from "react-toast-notifications";

import Button from "../Button";
import { Modal } from "../Modal";
import type { IProfile } from "types/Profile";

import CoverPreviewPanel from "./CoverPreviewPanel";
import CropHelperText from "./CropHelperText";
import { cropImageWithBackground } from "./cropImage";
import CropPanel, { ICropRatioOption } from "./CropPanel";
import LogoPreviewPanel from "./LogoPreviewPanel";

export type UploadVariant = "logo" | "cover";

export type UploadTab = "crop" | "preview";

export interface IUploadPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant: UploadVariant;
  image: string;
  aspect: number;
  format: "png" | "jpeg";
  maxWidth: number;
  onSave: (croppedImage: string) => Promise<void> | void;
  onCancel: () => void;
  profile: IProfile;
  ratioOptions?: ICropRatioOption[];
}

const TAB_LABELS: Record<UploadTab, string> = {
  crop: "Crop",
  preview: "Preview",
};

const TAB_KEYS: UploadTab[] = ["crop", "preview"];

const INITIAL_CROP: Point = { x: 0, y: 0 };

const MODAL_WIDTH = 447;

// Tallest layout = Logo Crop tab (1:1 cropper + RatioSelector + ZoomSlider + helper text + buttons).
// Applied to the modal wrapper so total height stays constant across tab + aspect switches.
// Using `height` (not `minHeight`) so `maxHeight: calc(100vh - 32px)` can actually cap on
// short viewports — `minHeight` would otherwise override the cap and overflow off-screen.
// Value verified visually against the rendered modal.
const MODAL_HEIGHT = 736;

const MODAL_CUSTOM_STYLES = {
  content: {
    padding: 0,
    width: `${MODAL_WIDTH}px`,
    minWidth: "auto",
    height: `${MODAL_HEIGHT}px`,
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 32px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
};

const UploadPreviewModal: React.FC<IUploadPreviewModalProps> = ({
  isOpen,
  onClose,
  variant,
  image,
  aspect,
  format,
  maxWidth,
  onSave,
  onCancel,
  profile,
  ratioOptions,
}) => {
  const [activeTab, setActiveTab] = useState<UploadTab>("crop");
  const [crop, setCrop] = useState<Point>(INITIAL_CROP);
  const [zoom, setZoom] = useState(1);
  const [cropperAspect, setCropperAspect] = useState(aspect);
  const croppedAreaPixelsRef = useRef<Area | undefined>(undefined);
  const [previewSrc, setPreviewSrc] = useState<string | undefined>(undefined);
  const [isComputing, setIsComputing] = useState(false);
  const [isCropReady, setIsCropReady] = useState(false);
  const { addToast } = useToasts();
  const [isCompactHeight] = useMediaQuery(
    `(min-width: ${chakraTheme.breakpoints.md}) and (max-height: 892px)`,
  );

  useEffect(() => {
    if (!isOpen) {
      setActiveTab("crop");
      setPreviewSrc(undefined);
      setCrop(INITIAL_CROP);
      setZoom(1);
      setCropperAspect(aspect);
      croppedAreaPixelsRef.current = undefined;
      setIsCropReady(false);
    }
  }, [isOpen, aspect]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const html = document.documentElement;
    html.style.overflowY = "hidden";
    return () => {
      html.style.overflowY = "";
    };
  }, [isOpen]);

  const handleCropAreaChange = useCallback((area: Area) => {
    croppedAreaPixelsRef.current = area;
    setIsCropReady(true);
    setPreviewSrc(undefined);
  }, []);

  const computePreview = useCallback(async (): Promise<string | null> => {
    if (!croppedAreaPixelsRef.current) return null;
    setIsComputing(true);
    try {
      return await cropImageWithBackground(
        image,
        croppedAreaPixelsRef.current,
        {
          format,
          maxWidth,
          backgroundColor:
            format === "jpeg" ? theme.palette.brand.primary.white : undefined,
        },
      );
    } catch (error) {
      console.error("Failed to compute preview", error);
      return null;
    } finally {
      setIsComputing(false);
    }
  }, [image, format, maxWidth]);

  useEffect(() => {
    if (activeTab !== "preview" || previewSrc) return undefined;
    let cancelled = false;
    computePreview().then(result => {
      if (!cancelled && result) {
        setPreviewSrc(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activeTab, previewSrc, computePreview]);

  const handleCancel = useCallback(() => {
    onCancel();
    onClose();
  }, [onCancel, onClose]);

  const handleSave = useCallback(async () => {
    const finalImage = previewSrc ?? (await computePreview());
    if (!finalImage) {
      addToast(
        "Could not save the image. We hit an error processing the crop. Please try again.",
        { appearance: "error" },
      );
      return;
    }
    await onSave(finalImage);
  }, [previewSrc, computePreview, onSave, addToast]);

  const title = useMemo(
    () => (variant === "logo" ? "Upload logo" : "Upload cover photo"),
    [variant],
  );

  const handleTabChange = useCallback((index: number) => {
    setActiveTab(TAB_KEYS[index]);
  }, []);

  const activeIndex = TAB_KEYS.indexOf(activeTab);

  return (
    <Modal
      isOpen={isOpen}
      label={title}
      onClose={handleCancel}
      closable={false}
      customStyles={MODAL_CUSTOM_STYLES}
    >
      {/* isLazy={false} keeps both panels in the React tree across tab switches.
          Chakra hides the inactive panel via display:none, which collapses its
          clientWidth to 0; that triggers a Cropper unmount/remount chain inside
          CropPanel via useElementWidthLayout + hasMeasured. See the comment in
          CropPanel.tsx handleMediaLoaded for the full walk-through. */}
      <Tabs
        variant="unstyled"
        isLazy={false}
        index={activeIndex}
        onChange={handleTabChange}
        display="flex"
        flexDirection="column"
        flex={1}
        minH={0}
      >
        <TabList pt="8px" borderBottom="none">
          {TAB_KEYS.map(key => (
            <Tab
              key={key}
              flex={1}
              py="12px"
              px="0"
              fontSize="14px"
              fontWeight={600}
              color={theme.palette.ui.greys.grey1}
              borderBottom="2px solid"
              borderColor={theme.palette.ui.greys.grey4}
              _selected={{
                color: theme.palette.brand.primary.black,
                borderColor: theme.palette.brand.primary.orange,
              }}
              _focus={{ outline: "none", boxShadow: "none" }}
              _focusVisible={{ outline: "none", boxShadow: "none" }}
            >
              {TAB_LABELS[key]}
            </Tab>
          ))}
        </TabList>

        <TabPanels flex={1} minH={0} overflowY="auto">
          <TabPanel p={0} pt="24px">
            <CropPanel
              image={image}
              cropperAspect={cropperAspect}
              setCropperAspect={setCropperAspect}
              crop={crop}
              setCrop={setCrop}
              zoom={zoom}
              setZoom={setZoom}
              onCropAreaChange={handleCropAreaChange}
              ratioOptions={ratioOptions}
              showHelperText={isCompactHeight}
            />
          </TabPanel>
          <TabPanel p={0} pt="24px">
            {variant === "logo" ? (
              <LogoPreviewPanel profile={profile} croppedLogo={previewSrc} />
            ) : (
              <CoverPreviewPanel profile={profile} croppedCover={previewSrc} />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Flex
        flexShrink={0}
        direction="column"
        gap="24px"
        px="16px"
        pt={isCompactHeight ? "16px" : 0}
        pb="16px"
      >
        {activeTab === "crop" && !isCompactHeight && (
          <CropHelperText mt="40px" />
        )}
        <Flex gap="8px">
          <Button
            variant="white"
            size="small"
            block
            onClick={handleCancel}
            disabled={isComputing}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="small"
            block
            onClick={handleSave}
            disabled={isComputing || !isCropReady}
            className="flex-1"
            data-testid="upload-preview-save-button"
          >
            Save
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default UploadPreviewModal;
